/**
 * Financing page. Built to match `design/Syntra/financing.html`:
 *
 *   - Header (title + subtitle).
 *   - KPI row (Total Loan Balance · Monthly Debt Service · Avg Interest Rate ·
 *     Financed Properties).
 *   - Table — one row per owned property; click-to-edit.
 *   - Slide-in editor drawer (Status · Short/Long tabs · Lender · Loan Dates ·
 *     Loan Terms · Computed tiles · Holding Costs).
 *
 * Financing is sourced from `property_setting` strategy blobs (the same model
 * the Properties page + find-detail use), joined to the owned-properties list
 * from `useOwnedProperties`. The editor persists back via `useDealSettings`.
 */
import { useMemo, useState } from "react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Banknote } from "lucide-react";

import { FinancingEditor } from "@/components/financing/FinancingEditor";
import { FinancingKpiRow } from "@/components/financing/FinancingKpiRow";
import {
  FinancingLoansTable,
  type FinancingTableRow,
} from "@/components/financing/FinancingLoansTable";
import {
  aggregateFinancingKpis,
  computeOwnedLoanInfo,
} from "@/components/financing/financing-metrics";
import { useOwnedProperties } from "@/hooks/use-owned-properties";

export default function Financing() {
  const { items, isLoading, refetch } = useOwnedProperties();

  const rows = useMemo<FinancingTableRow[]>(
    () =>
      items.map((item) => ({
        pipelinePropertyId: item.id,
        info: computeOwnedLoanInfo({
          settings: item.settings,
          strategy: item.strategy,
          fallbackPurchase: item.purchasePrice,
          loan: item.loan,
        }),
        property: item.property,
        strategy: item.strategy,
      })),
    [items],
  );

  const kpis = useMemo(
    () => aggregateFinancingKpis(rows.map((r) => r.info)),
    [rows],
  );

  // Which property's financing is open in the editor drawer.
  const [editingId, setEditingId] = useState<string | null>(null);
  const editingItem = items.find((i) => i.id === editingId) ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          Financing
        </h1>
        <p className="text-sm text-muted-foreground">
          Track the loans financing every property you own.
        </p>
      </div>

      <FinancingKpiRow kpis={kpis} />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-md" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card className="shadow-soft">
          <CardContent className="py-20 text-center">
            <Banknote className="mx-auto mb-3 h-12 w-12 text-muted-foreground/40" />
            <h3 className="text-base font-bold text-foreground">
              No loans to track yet
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Loans appear here once you click{" "}
              <span className="font-semibold text-primary">Move to Rehab</span>{" "}
              on a Closed Won deal in the Pipeline.
            </p>
          </CardContent>
        </Card>
      ) : (
        <FinancingLoansTable
          rows={rows}
          onRowClick={(row) => setEditingId(row.pipelinePropertyId)}
        />
      )}

      <FinancingEditor
        item={editingItem}
        onClose={() => setEditingId(null)}
        onSaved={refetch}
      />
    </div>
  );
}
